/**
 * Sync Service
 * Handles synchronization between IndexedDB and the server
 */

import * as db from './indexedDB';
import { eventsApi, expensesApi, budgetsApi, userApi, attachmentsApi } from './api';
import { preCacheAttachments, initAttachmentCache, uploadPendingAttachments, getPendingAttachments, getAllPendingTargetIds, clearCacheByEventId, clearPendingAttachments } from './attachmentCache';

// Generate temporary IDs for offline-created items
export function generateTempId() {
  return `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

// Check if an ID is temporary (created offline)
export function isTempId(id) {
  return typeof id === 'string' && id.startsWith('temp_');
}

/**
 * Sync all data from server to IndexedDB
 * Called when coming online or on initial load
 */
export async function syncFromServer() {
  console.log('[Sync] Starting full sync from server...');
  
  try {
    // Initialize attachment cache DB
    await initAttachmentCache();
    
    // Fetch all data from server
    const [events, expenses, budgets, user] = await Promise.all([
      eventsApi.list(),
      expensesApi.list(),
      budgetsApi.list(),
      userApi.getMe(),
    ]);

    // Store in IndexedDB
    await db.syncFromServer(events, expenses, budgets, user);
    
    // Pre-cache attachments for upcoming events (in background)
    preCacheAttachments(events, attachmentsApi).catch(err => {
      console.warn('[Sync] Attachment pre-cache failed:', err);
    });
    
    console.log('[Sync] Full sync complete');
    return { success: true };
  } catch (error) {
    console.error('[Sync] Failed to sync from server:', error);
    
    // Re-throw auth errors so they can be handled by OfflineContext
    if (error.isAuthExpired) {
      throw error;
    }
    
    return { success: false, error };
  }
}

/**
 * Process the sync queue - push offline changes to server
 */
export async function processSyncQueue() {
  const queue = await db.getSyncQueue();
  
  if (queue.length === 0) {
    console.log('[Sync] No items in queue');
  } else {
    console.log(`[Sync] Processing ${queue.length} queued items...`);
  }
  
  let processed = 0;
  let failed = 0;
  const idMappings = {}; // Maps temp IDs to real IDs
  
  for (const item of queue) {
    try {
      await processQueueItem(item, idMappings);
      await db.removeFromSyncQueue(item.id);
      processed++;
    } catch (error) {
      console.error(`[Sync] Failed to process queue item:`, item, error);
      
      // Update retry count
      item.retryCount = (item.retryCount || 0) + 1;
      item.lastError = error.message;
      
      if (item.retryCount >= 3) {
        // Give up after 3 retries
        console.error(`[Sync] Giving up on item after ${item.retryCount} retries:`, item);
        await db.removeFromSyncQueue(item.id);
      } else {
        await db.updateSyncQueueItem(item);
      }
      
      failed++;
    }
  }
  
  // Upload any remaining pending attachments (for existing events)
  const attachmentResults = await uploadAllPendingAttachments(idMappings);
  
  console.log(`[Sync] Queue processing complete: ${processed} processed, ${failed} failed, ${attachmentResults.uploaded} attachments uploaded`);
  return { processed, failed, idMappings, attachmentResults };
}

/**
 * Upload all pending attachments
 * - For temp IDs: resolve to real IDs using idMappings (handled in processEventAction)
 * - For real IDs (existing events): upload directly
 */
async function uploadAllPendingAttachments(idMappings) {
  const results = { uploaded: 0, failed: 0, errors: [] };
  
  try {
    const targets = await getAllPendingTargetIds();
    
    if (targets.length === 0) {
      return results;
    }
    
    console.log(`[Sync] Uploading pending attachments for ${targets.length} targets...`);
    
    for (const { targetTempId, targetType } of targets) {
      // Skip temp IDs - they're handled by processEventAction during event creation
      if (isTempId(targetTempId)) {
        // Check if we have a mapping (event was just created)
        if (!idMappings[targetTempId]) {
          console.log(`[Sync] Skipping pending attachments for ${targetTempId} - no ID mapping yet`);
          continue;
        }
      }
      
      // Resolve the real ID
      const realId = idMappings[targetTempId] || targetTempId;
      
      try {
        const uploadResult = await uploadPendingAttachments(targetTempId, realId, targetType, attachmentsApi);
        results.uploaded += uploadResult.succeeded;
        results.failed += uploadResult.failed;
        results.errors.push(...uploadResult.errors);
      } catch (err) {
        console.error(`[Sync] Failed to upload pending attachments for ${targetTempId}:`, err);
        results.failed++;
        results.errors.push(err.message);
      }
    }
  } catch (err) {
    console.error('[Sync] Failed to get pending attachment targets:', err);
  }
  
  return results;
}

/**
 * Process a single queue item
 */
async function processQueueItem(item, idMappings) {
  const { type, action, data, entityId } = item;
  
  // Replace temp IDs with real IDs from previous syncs
  let resolvedId = entityId;
  if (isTempId(entityId) && idMappings[entityId]) {
    resolvedId = idMappings[entityId];
  }
  
  // Also resolve any foreign key references (e.g., event_id in expenses)
  const resolvedData = { ...data };
  if (resolvedData.event_id && isTempId(resolvedData.event_id) && idMappings[resolvedData.event_id]) {
    resolvedData.event_id = idMappings[resolvedData.event_id];
  }
  
  switch (type) {
    case 'event':
      return processEventAction(action, resolvedId, resolvedData, idMappings);
    case 'expense':
      return processExpenseAction(action, resolvedId, resolvedData, idMappings);
    case 'budget':
      return processBudgetAction(action, resolvedId, resolvedData, idMappings);
    default:
      throw new Error(`Unknown sync type: ${type}`);
  }
}

async function processEventAction(action, id, data, idMappings) {
  switch (action) {
    case 'create': {
      const result = await eventsApi.create(data);
      const tempId = id; // Use the id parameter which contains the temp ID from sync queue
      
      // Map temp ID to real ID
      if (isTempId(tempId)) {
        idMappings[tempId] = result.id;
        // Update local IndexedDB with real ID
        await db.deleteById('events', tempId);
        await db.put('events', result);
        
        // Upload any pending attachments for this event
        try {
          const pending = await getPendingAttachments(tempId);
          if (pending.length > 0) {
            console.log(`[Sync] Uploading ${pending.length} pending attachments for event ${result.id}`);
            const uploadResult = await uploadPendingAttachments(tempId, result.id, 'event', attachmentsApi);
            if (uploadResult.failed > 0) {
              console.warn(`[Sync] Some attachments failed to upload:`, uploadResult.errors);
            }
          }
        } catch (err) {
          console.error('[Sync] Failed to upload pending attachments:', err);
        }
      }
      return result;
    }
    case 'update':
      return eventsApi.update(id, data);
    case 'delete':
      return eventsApi.delete(id);
    default:
      throw new Error(`Unknown event action: ${action}`);
  }
}

async function processExpenseAction(action, id, data, idMappings) {
  switch (action) {
    case 'create': {
      const result = await expensesApi.create(data);
      const tempId = id; // Use the id parameter which contains the temp ID from sync queue
      if (isTempId(tempId)) {
        idMappings[tempId] = result.id;
        await db.deleteById('expenses', tempId);
        await db.put('expenses', result);
      }
      return result;
    }
    case 'update':
      return expensesApi.update(id, data);
    case 'delete':
      return expensesApi.delete(id);
    default:
      throw new Error(`Unknown expense action: ${action}`);
  }
}

async function processBudgetAction(action, id, data, idMappings) {
  switch (action) {
    case 'create':
    case 'update': {
      // Budgets use upsert (set)
      const result = await budgetsApi.set(data);
      const tempId = id; // Use the id parameter which contains the temp ID from sync queue
      if (isTempId(tempId)) {
        idMappings[tempId] = result.id;
        await db.deleteById('budgets', tempId);
        await db.put('budgets', result);
      }
      return result;
    }
    case 'delete':
      return budgetsApi.delete(id);
    default:
      throw new Error(`Unknown budget action: ${action}`);
  }
}

/**
 * Offline-aware API operations
 * These check online status and either hit the API or queue for later
 */

export async function createEvent(eventData, isOnline, providedTempId = null) {
  const tempId = providedTempId || generateTempId();
  const event = { ...eventData, id: tempId };
  
  // Always save to IndexedDB first
  await db.put('events', event);
  
  if (isOnline) {
    try {
      const result = await eventsApi.create(eventData);
      // Replace temp with real data
      await db.deleteById('events', tempId);
      await db.put('events', result);
      return result;
    } catch (error) {
      // Failed but we have local copy, queue for retry
      await db.addToSyncQueue({
        type: 'event',
        action: 'create',
        entityId: tempId,
        data: eventData,
      });
      return event;
    }
  } else {
    // Offline - queue for later
    await db.addToSyncQueue({
      type: 'event',
      action: 'create',
      entityId: tempId,
      data: eventData,
    });
    return event;
  }
}

export async function updateEvent(id, eventData, isOnline) {
  const updatedEvent = { ...eventData, id };
  
  // Update IndexedDB
  await db.put('events', updatedEvent);
  
  if (isOnline && !isTempId(id)) {
    try {
      const result = await eventsApi.update(id, eventData);
      await db.put('events', result);
      return result;
    } catch (error) {
      await db.addToSyncQueue({
        type: 'event',
        action: 'update',
        entityId: id,
        data: eventData,
      });
      return updatedEvent;
    }
  } else {
    await db.addToSyncQueue({
      type: 'event',
      action: 'update',
      entityId: id,
      data: eventData,
    });
    return updatedEvent;
  }
}

export async function deleteEvent(id, isOnline) {
  // Remove linked expenses from IndexedDB (cascade delete)
  await db.deleteByIndex('expenses', 'event_id', id);
  
  // Clear cached attachments for this event
  try {
    await clearCacheByEventId(id);
    await clearPendingAttachments(id);
  } catch (err) {
    console.warn('[Sync] Failed to clear attachment cache:', err);
    // Continue with deletion even if cache clear fails
  }
  
  // Remove from IndexedDB
  await db.deleteById('events', id);
  
  if (isOnline && !isTempId(id)) {
    try {
      await eventsApi.delete(id);
    } catch (error) {
      await db.addToSyncQueue({
        type: 'event',
        action: 'delete',
        entityId: id,
        data: null,
      });
    }
  } else if (!isTempId(id)) {
    // Only queue delete if it's a real item (not temp)
    await db.addToSyncQueue({
      type: 'event',
      action: 'delete',
      entityId: id,
      data: null,
    });
  }
  // If it's a temp ID, it was never on server, so just local delete is enough
}

export async function createExpense(expenseData, isOnline) {
  const tempId = generateTempId();
  const expense = { ...expenseData, id: tempId };
  
  await db.put('expenses', expense);
  
  if (isOnline) {
    try {
      const result = await expensesApi.create(expenseData);
      await db.deleteById('expenses', tempId);
      await db.put('expenses', result);
      return result;
    } catch (error) {
      await db.addToSyncQueue({
        type: 'expense',
        action: 'create',
        entityId: tempId,
        data: expenseData,
      });
      return expense;
    }
  } else {
    await db.addToSyncQueue({
      type: 'expense',
      action: 'create',
      entityId: tempId,
      data: expenseData,
    });
    return expense;
  }
}

export async function updateExpense(id, expenseData, isOnline) {
  const updatedExpense = { ...expenseData, id };
  
  await db.put('expenses', updatedExpense);
  
  if (isOnline && !isTempId(id)) {
    try {
      const result = await expensesApi.update(id, expenseData);
      await db.put('expenses', result);
      return result;
    } catch (error) {
      await db.addToSyncQueue({
        type: 'expense',
        action: 'update',
        entityId: id,
        data: expenseData,
      });
      return updatedExpense;
    }
  } else {
    await db.addToSyncQueue({
      type: 'expense',
      action: 'update',
      entityId: id,
      data: expenseData,
    });
    return updatedExpense;
  }
}

export async function deleteExpense(id, isOnline) {
  await db.deleteById('expenses', id);
  
  if (isOnline && !isTempId(id)) {
    try {
      await expensesApi.delete(id);
    } catch (error) {
      await db.addToSyncQueue({
        type: 'expense',
        action: 'delete',
        entityId: id,
        data: null,
      });
    }
  } else if (!isTempId(id)) {
    await db.addToSyncQueue({
      type: 'expense',
      action: 'delete',
      entityId: id,
      data: null,
    });
  }
}

export async function setBudget(budgetData, isOnline) {
  const tempId = budgetData.id || generateTempId();
  const budget = { ...budgetData, id: tempId };
  
  await db.put('budgets', budget);
  
  if (isOnline) {
    try {
      const result = await budgetsApi.set(budgetData);
      if (tempId !== result.id) {
        await db.deleteById('budgets', tempId);
      }
      await db.put('budgets', result);
      return result;
    } catch (error) {
      await db.addToSyncQueue({
        type: 'budget',
        action: 'create',
        entityId: tempId,
        data: budgetData,
      });
      return budget;
    }
  } else {
    await db.addToSyncQueue({
      type: 'budget',
      action: 'create',
      entityId: tempId,
      data: budgetData,
    });
    return budget;
  }
}

export async function deleteBudget(id, isOnline) {
  await db.deleteById('budgets', id);
  
  if (isOnline && !isTempId(id)) {
    try {
      await budgetsApi.delete(id);
    } catch (error) {
      await db.addToSyncQueue({
        type: 'budget',
        action: 'delete',
        entityId: id,
        data: null,
      });
    }
  } else if (!isTempId(id)) {
    await db.addToSyncQueue({
      type: 'budget',
      action: 'delete',
      entityId: id,
      data: null,
    });
  }
}

/**
 * Get data - IndexedDB first for instant display, then sync from server
 * This ensures PWA/standalone mode always has data to show immediately
 */
export async function getEvents(isOnline, params = {}) {
  // Ensure IndexedDB is initialized
  await db.initDB();
  
  // Always try IndexedDB first for instant display
  let cachedEvents = [];
  try {
    cachedEvents = await db.getAll('events');
    console.log('[Sync] Got cached events:', cachedEvents.length);
  } catch (e) {
    console.log('[Sync] IndexedDB not ready for events:', e.message);
  }
  
  if (isOnline) {
    try {
      const events = await eventsApi.list(params);
      // Update IndexedDB cache
      if (events && events.length > 0) {
        await db.putMany('events', events);
      }
      return events;
    } catch (error) {
      console.log('[Sync] API failed, using cached events:', error.message);
      // Return cached data on error
      return cachedEvents;
    }
  }
  
  return cachedEvents;
}

export async function getExpenses(isOnline, params = {}) {
  // Ensure IndexedDB is initialized
  await db.initDB();
  
  // Always try IndexedDB first
  let cachedExpenses = [];
  try {
    cachedExpenses = await db.getAll('expenses');
    console.log('[Sync] Got cached expenses:', cachedExpenses.length);
  } catch (e) {
    console.log('[Sync] IndexedDB not ready for expenses:', e.message);
  }
  
  if (isOnline) {
    try {
      const expenses = await expensesApi.list(params);
      if (expenses && expenses.length > 0) {
        await db.putMany('expenses', expenses);
      }
      return expenses;
    } catch (error) {
      console.log('[Sync] API failed, using cached expenses:', error.message);
      return cachedExpenses;
    }
  }
  
  return cachedExpenses;
}

export async function getBudgets(isOnline) {
  // Ensure IndexedDB is initialized
  await db.initDB();
  
  // Always try IndexedDB first
  let cachedBudgets = [];
  try {
    cachedBudgets = await db.getAll('budgets');
    console.log('[Sync] Got cached budgets:', cachedBudgets.length);
  } catch (e) {
    console.log('[Sync] IndexedDB not ready for budgets:', e.message);
  }
  
  if (isOnline) {
    try {
      const budgets = await budgetsApi.list();
      if (budgets && budgets.length > 0) {
        await db.putMany('budgets', budgets);
      }
      return budgets;
    } catch (error) {
      console.log('[Sync] API failed, using cached budgets:', error.message);
      return cachedBudgets;
    }
  }
  
  return cachedBudgets;
}

export async function getBudgetSummary(isOnline) {
  // Ensure IndexedDB is initialized
  await db.initDB();
  
  if (isOnline) {
    try {
      const summary = await budgetsApi.getSummary();
      
      // Cache the full summary for offline use
      await db.setMetadata('budgetSummaryCache', summary);
      
      // Also cache individual budget items for other operations
      if (summary.categories) {
        const budgetItems = Object.entries(summary.categories).map(([category, data]) => ({
          id: `budget_${category}`,
          category,
          amount: data.budget,
          currency: summary.currency || 'EUR',
        }));
        await db.putMany('budgets', budgetItems);
      }
      
      return summary;
    } catch (error) {
      console.log('[Sync] API failed, using cached budget summary');
      return getOfflineBudgetSummary();
    }
  }
  return getOfflineBudgetSummary();
}

async function getOfflineBudgetSummary() {
  // First try to get cached summary
  try {
    const cachedSummary = await db.getMetadata('budgetSummaryCache');
    if (cachedSummary) {
      console.log('[Sync] Using cached budget summary');
      return cachedSummary;
    }
  } catch (e) {
    console.log('[Sync] No cached summary, calculating locally');
  }
  
  // Fall back to calculating from stored data
  return calculateLocalBudgetSummary();
}

async function calculateLocalBudgetSummary() {
  let budgets = [];
  let expenses = [];
  
  try {
    budgets = await db.getAll('budgets');
    expenses = await db.getAll('expenses');
  } catch (e) {
    console.log('[Sync] IndexedDB not ready for budget summary');
  }
  
  // Build categories object matching server response format
  const categoryList = ['transport', 'accommodation', 'visas', 'activities', 'food_drink', 'miscellaneous'];
  const categories = {};
  
  let totalBudget = 0;
  let totalPlanned = 0;
  let totalActual = 0;
  
  for (const cat of categoryList) {
    const budget = budgets.find(b => b.category === cat);
    const categoryExpenses = expenses.filter(e => e.category === cat);
    
    const budgetAmount = budget?.amount || 0;
    const plannedAmount = categoryExpenses
      .filter(e => e.is_planned)
      .reduce((sum, e) => sum + (e.amount || 0), 0);
    const actualAmount = categoryExpenses
      .filter(e => !e.is_planned)
      .reduce((sum, e) => sum + (e.amount || 0), 0);
    
    categories[cat] = {
      budget: budgetAmount,
      planned: plannedAmount,
      actual: actualAmount,
      remaining_from_budget: budgetAmount - plannedAmount - actualAmount,
      planned_vs_budget_pct: budgetAmount > 0 ? Math.round((plannedAmount / budgetAmount) * 100) : 0,
      actual_vs_budget_pct: budgetAmount > 0 ? Math.round((actualAmount / budgetAmount) * 100) : 0,
      total_spent_pct: budgetAmount > 0 ? Math.round(((plannedAmount + actualAmount) / budgetAmount) * 100) : 0,
    };
    
    totalBudget += budgetAmount;
    totalPlanned += plannedAmount;
    totalActual += actualAmount;
  }
  
  return {
    categories,
    totals: {
      budget: totalBudget,
      planned: totalPlanned,
      actual: totalActual,
      remaining: totalBudget - totalPlanned - totalActual,
      planned_vs_budget_pct: totalBudget > 0 ? Math.round((totalPlanned / totalBudget) * 100) : 0,
      actual_vs_budget_pct: totalBudget > 0 ? Math.round((totalActual / totalBudget) * 100) : 0,
      total_spent_pct: totalBudget > 0 ? Math.round(((totalPlanned + totalActual) / totalBudget) * 100) : 0,
    },
    currency: 'EUR',
  };
}
